/**
 * Single source of truth for app colors.
 *
 *  - `brand`          — Utah Health brand palette (semantic + legacy layout keys).
 *  - `utahRedRamp`    — Fluent UI v9 BrandVariants ramp for theme construction.
 *  - `shadow`         — Elevation / box-shadow tokens.
 *  - `overlay`        — Translucent effect colors.
 *  - `buttonGradient` — Gradient stop colors for AppButton variants.
 *
 * The legacy keys (utahRed, utahRedDark, navHeight, maxContentWidth, fontFamily,
 * letterSpacing*) are kept here so existing imports from brandTokens.ts keep
 * compiling without modification.
 */

import type { BrandVariants } from '@fluentui/react-components';

// ─── Utah Health brand palette ───────────────────────────────────────────────
export const brand = {
  // ── Semantic color keys (new / reference-aligned) ──────────────────────────
  red: '#BE0000',
  redDark: '#A00000',
  redDarker: '#890000',
  bodyText: '#1A1A1A',
  granitePeak: '#708E99',
  gray50: '#939598',
  slateGray: '#5A6872',
  saltFlat: '#E2E6E6',
  background: '#F5F7F7',
  lakePowell: '#4F868E',
  mountainGreen: '#6CC24A',
  wasatchSunrise: '#FFB81D',
  coralPink: '#E4937B',
  aspenYellow: '#EFC041',
  white: '#FFFFFF',
  black: '#000000',
  error: '#890000',

  // ── Legacy keys — required so off-limits pages keep compiling ───────────────
  utahRed: '#BE0000',
  utahRedDark: '#A00000',
  navHeight: '64px',
  maxContentWidth: '1320px',
  fontFamily: "'Sofia Pro', 'Century Gothic', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  letterSpacingHeadline: '0.1em',
  letterSpacingWide: '0.06em',
  letterSpacingTight: '0.02em',
} as const;

// ─── Fluent UI v9 brand ramp (Utah red) ──────────────────────────────────────
// Convention: 10 is the DARKEST shade, 160 is the LIGHTEST tint.
// Index 80 is the primary (brand.red). Consumed by theme/utahHealthTheme.ts.
export const utahRedRamp: BrandVariants = {
  10: '#1F0000',
  20: '#3A0000',
  30: '#530000',
  40: '#6B0000',
  50: '#840000',
  60: '#9D0000',
  70: '#B30000',
  80: brand.red, // primary
  90: '#C61F1F',
  100: '#CE4040',
  110: '#D76161',
  120: '#DE8080',
  130: '#E69E9E',
  140: '#EEBDBD',
  150: '#F6DBDB',
  160: '#FCF2F2',
};

// ─── Elevation shadows ────────────────────────────────────────────────────────
export const shadow = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  card: '0px 0px 0px 1px rgba(0, 0, 0, 0.06), 0px 1px 2px -1px rgba(0, 0, 0, 0.06), 0px 2px 4px 0px rgba(0, 0, 0, 0.04)',
  cardHover: '0px 0px 0px 1px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.1)',
  tooltip: '0 4px 12px rgba(0,0,0,0.10)',
  // AppButton brand elevation states
  button: '0 2px 6px rgba(0, 0, 0, 0.15)',
  buttonHover: '0 4px 10px rgba(0, 0, 0, 0.2)',
  buttonActive: '0 1px 3px rgba(0, 0, 0, 0.12)',
} as const;

// ─── Translucent overlays / effect colors ────────────────────────────────────
export const overlay = {
  scrollThumb: 'rgba(0, 0, 0, 0.25)',
  mutedBorder: `color-mix(in srgb, ${brand.slateGray} 25%, transparent)`,
} as const;

// ─── Button fill gradient stops ───────────────────────────────────────────────
export const buttonGradient = {
  redLight: '#D40000',
  redDark: '#A30000',
  greenLight: '#7ED15C',
  greenDark: '#5AA83C',
  yellowLight: '#FFC94D',
  yellowDark: '#E8A40F',
} as const;
